// Color palettes
const defaultColors = {
  // Primary Colors
  primary: '#0294df',
  primaryLight: '#e6f4fc',
  primaryHover: '#0288cd',

  // Neutral Colors
  text: {
    primary: '#1A1A1A',
    secondary: '#6B6B6B',
    tertiary: '#767676', // WCAG AA compliant (4.54:1 contrast with white)
    inverse: '#FFFFFF',
  },

  background: {
    primary: '#FFFFFF',
    secondary: '#FAFAFA',
    tertiary: '#F5F5F5',
    inverse: '#1A1A1A',
  },

  border: {
    default: '#E5E5E5',
    light: '#F0F0F0',
    focus: '#0294df',
    error: '#FF3B30',
    primary: '#E5E5E5',
  },

  // Semantic Colors
  success: '#00B341',
  successLight: '#00b34114',
  warning: '#FF9500',
  warningLight: '#ffc374ff',
  danger: '#FF3B30',
  dangerLight: '#ff3b3014',
  info: '#007AFF',

  // Tag Colors (10% opacity backgrounds)
  tags: {
    blue: 'rgba(2, 148, 223, 0.1)',
    green: 'rgba(0, 179, 65, 0.1)',
    purple: 'rgba(128, 90, 213, 0.1)',
    orange: 'rgba(255, 149, 0, 0.1)',
    red: 'rgba(255, 59, 48, 0.1)',
    yellow: 'rgba(255, 204, 0, 0.1)',
    teal: 'rgba(90, 200, 250, 0.1)',
    pink: 'rgba(255, 45, 85, 0.1)',
    gray: 'rgba(142, 142, 147, 0.1)',
    steel: 'rgba(100, 116, 139, 0.1)',
    bronze: 'rgba(146, 64, 14, 0.1)',
    forest: 'rgba(22, 101, 52, 0.1)',
    deepBlue: 'rgba(30, 58, 138, 0.1)',
    wine: 'rgba(159, 18, 57, 0.1)',
  },

  // Activity type colors
  activity: {
    call: '#ffc374ff',
    email: '#007AFF',
    meeting: '#FF9500',
    task: '#5856D6',
  },

  // Surface colors for overlays
  surface: {
    overlay: 'rgba(0, 0, 0, 0.5)',
  },
}

// Daltonism-friendly color palette (high contrast, colorblind-safe)
const daltonismColors = {
  // Primary Colors - Using blue which is universally distinguishable
  primary: '#0066CC',
  primaryLight: '#E6F0FF',
  primaryHover: '#0052A3',

  // Neutral Colors - Higher contrast
  text: {
    primary: '#000000',
    secondary: '#4D4D4D',
    tertiary: '#737373', // WCAG AA compliant (4.65:1 contrast with white)
    inverse: '#FFFFFF',
  },

  background: {
    primary: '#FFFFFF',
    secondary: '#F5F5F5',
    tertiary: '#EBEBEB',
    inverse: '#000000',
  },

  border: {
    default: '#CCCCCC',
    light: '#E0E0E0',
    focus: '#0066CC',
    error: '#CC00CC',
    primary: '#CCCCCC',
  },

  // Semantic Colors - Avoiding red/green confusion
  success: '#0066CC', // Blue instead of green
  successLight: '#B3D9FF',
  warning: '#FF8800', // Orange is safe
  warningLight: '#FFD699',
  danger: '#CC00CC', // Magenta instead of red
  dangerLight: '#F2B3F2',
  info: '#0066CC',

  // Tag Colors - High contrast, colorblind-safe palette
  tags: {
    blue: 'rgba(0, 102, 204, 0.15)',
    green: 'rgba(0, 102, 204, 0.15)', // Using blue
    purple: 'rgba(102, 51, 153, 0.15)',
    orange: 'rgba(255, 136, 0, 0.15)',
    red: 'rgba(204, 0, 204, 0.15)', // Using magenta
    yellow: 'rgba(255, 204, 0, 0.15)',
    teal: 'rgba(0, 153, 153, 0.15)',
    pink: 'rgba(255, 102, 204, 0.15)',
    gray: 'rgba(128, 128, 128, 0.15)',
    steel: 'rgba(100, 116, 139, 0.15)',
    bronze: 'rgba(146, 64, 14, 0.15)',
    forest: 'rgba(22, 101, 52, 0.15)',
    deepBlue: 'rgba(30, 58, 138, 0.15)',
    wine: 'rgba(159, 18, 57, 0.15)',
  },

  // Activity type colors - Colorblind-safe
  activity: {
    call: '#FF8800',
    email: '#0066CC',
    meeting: '#FFB347',
    task: '#663399',
  },

  // Surface colors for overlays
  surface: {
    overlay: 'rgba(0, 0, 0, 0.6)',
  },
}

// Base theme configuration (shared across all themes)
const baseThemeConfig = {
  typography: {
    fontFamily: {
      sans: "'Poppins', 'Inter', 'Segoe UI', Roboto, sans-serif",
      mono: "'SF Mono', Consolas, 'Liberation Mono', Menlo, Courier, monospace",
    },
    fontSize: {
      // Label Text
      label: '10px',
      // Micro Text
      micro: '10px',
      // Small Text
      small: '11px',
      // Body Text
      body: '12px',
      // Section Headers
      section: '13px',
      // Page Titles
      title: '15px',
      // Hero Values
      hero: '23px',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.6,
    },
    letterSpacing: {
      normal: '0',
      uppercase: '0.5px',
    },
  },

  spacing: {
    micro: '2px',
    xsmall: '3px',
    small: '6px',
    default: '10px',
    medium: '12px',
    large: '20px',
    xlarge: '26px',
    huge: '38px',
  },

  borderRadius: {
    small: '3px', // badges, small buttons
    default: '5px', // buttons, inputs, tags
    medium: '6px', // cards, panels
    large: '8px', // single control buttons
    xlarge: '10px', // control groups, modals
    round: '20px', // FAB buttons
    circle: '50%', // avatars, round buttons
  },

  shadows: {
    sm: '0 1px 2px rgba(0,0,0,0.04)',
    default: '0 2px 8px rgba(0,0,0,0.08)',
    md: '0 1px 4px rgba(0,0,0,0.15)',
    lg: '0 4px 20px rgba(0,0,0,0.15)',
    xl: '0 4px 24px rgba(0,0,0,0.12)',
    // Special shadows
    focus: '0 0 0 3px rgba(2, 148, 223, 0.25)',
    error: '0 0 0 3px rgba(255, 59, 48, 0.25)',
    fab: '0 2px 8px rgba(2, 148, 223,0.3)',
  },

  transitions: {
    fast: '0.2s ease',
    default: '0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    medium: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  breakpoints: {
    // Legacy breakpoints (deprecated - use mobile/tablet/desktop for new code)
    /** @deprecated Use mobile instead */
    smallMobile: '320px',
    /** @deprecated Use mobile instead */
    largeMobile: '480px',
    // Modern breakpoints (mobile-first approach)
    mobile: '640px', // Viewport >= 640px (tablet and up)
    tablet: '1024px', // Viewport >= 1024px (desktop and up)
    desktop: '1280px', // Viewport >= 1280px (wide desktop)
    // Extended breakpoints for specific needs
    wideDesktop: '1440px',
    extraWide: '1600px',
  },

  // Touch target sizes (WCAG 2.1 compliant)
  touchTargets: {
    minimum: '44px', // Minimum touch target size
    comfortable: '48px', // Comfortable touch target size
    large: '52px', // Large touch target for primary actions
  },

  zIndex: {
    elevated: 1,
    floating: 2,
    panels: 10,
    sticky: 100,
    sidebar: 900,
    // Keep nonmodal details above map controls but below global navigation.
    detailPanel: 1020,
    header: 1030,
    dropdown: 1050,
    modal: 1100,
    popover: 1200,
    toast: 1300,
    tooltip: 1400,
  },

  layout: {
    appHeaderHeight: '48px',
    propertyHeaderHeight: '84px',
    propertyHeaderHeightMobile: 'auto',
    sidebarWidth: '160px',
    contentPadding: '26px',
    contentPaddingMobile: '16px',
    cardSpacing: '20px',
    gridGapMobile: '16px',
    gridGapDesktop: '26px',
  },

  components: {
    // Button specific styles
    button: {
      padding: {
        primary: '5px 10px',
        icon: '6px',
      },
      height: {
        small: '26px',
        default: '28px',
        medium: '32px',
        large: '38px',
        icon: '26px',
        fab: '38px',
      },
      // Mobile heights (touch-friendly)
      heightMobile: {
        small: '44px',
        default: '44px',
        medium: '48px',
        large: '52px',
        icon: '44px',
      },
      fontSize: '10px',
    },
    // Map controls
    mapControl: {
      size: '32px',
      groupRadius: '10px',
      dividerColor: 'rgba(0,0,0,0.06)',
    },
    // Property tags
    tag: {
      padding: '3px 8px',
      fontSize: '10px',
      iconSize: '10px',
    },
    // Cards
    card: {
      headerPadding: '16px 20px',
      bodyPadding: '20px',
    },
    // Toggle switches
    toggle: {
      width: '36px',
      height: '21px',
    },
    // Form elements
    input: {
      height: {
        small: '26px',
        default: '32px',
        large: '38px',
      },
      heightMobile: {
        small: '44px',
        default: '48px',
        large: '52px',
      },
      padding: '5px 10px',
    },
    // Badges
    badge: {
      padding: '3px 6px',
      fontSize: '10px',
    },
    // Charts
    radar: {
      sizeDesktop: '64px',
      sizeMobile: '60px',
    },
    // Tables
    table: {
      cellPadding: '12px',
      cellPaddingMobile: '12px',
    },
    // Progress bars
    progress: {
      height: '3px',
    },
    // Map specific
    map: {
      resizableMinWidth: '256px',
      resizableMaxWidth: '640px',
      layerPanelWidth: '224px',
    },
  },

  animations: {
    popIn: `
      @keyframes popIn {
        from {
          opacity: 0;
          transform: scale(0.8) translateY(10px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
    `,
    fadeIn: `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateX(-50%) translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      }
    `,
    slideInLeft: `
      @keyframes slideInLeft {
        from {
          transform: translateX(-100%);
        }
        to {
          transform: translateX(0);
        }
      }
    `,
    rotate: `
      @keyframes rotate {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(45deg);
        }
      }
    `,
  },
}

// Export default theme
export const theme = {
  ...baseThemeConfig,
  colors: defaultColors,
}

// Export daltonism-friendly theme
export const daltonismTheme = {
  ...baseThemeConfig,
  colors: daltonismColors,
}

export type Theme = typeof theme
export type ThemeVariant = 'default' | 'daltonism'
